import cn from "classnames";
import { useCallback, useId } from "react";
import { A11y, A11yRoot } from "../a11y";
import { Flyout } from "../flyout";
import {
  useClickOutside,
  useUpdateEffect,
  useLatestRef,
} from "../common/hooks";
import { DropdownItem } from "./dropdown-item";
import { ALIGNS } from "./constants";
import { useDropdownA11y, useScrollFades } from "./hooks";
import { useDropdownPosition } from "./hooks/use-position";
import type { DropdownProps } from "./types";
import styles from "./styles.module.scss";

export const Dropdown = ({
  items,
  children,
  className,
  flyoutClassName,
  shown = false,
  align = ALIGNS.RIGHT,
  onOutsideClick,
  onOutsideWheel,
  dataTestId = "dropdown",
}: DropdownProps) => {
  const id = useId();

  const { position, flyoutPositions, handleChangePosition, handleHide } =
    useDropdownPosition({ align });

  const {
    dropdownRef,
    hasFadeTop,
    hasFadeBottom,
    handleScroll,
    dropdownCallbackRef,
  } = useScrollFades();

  const {
    enabledItemsData,
    a11yCurrentChildIndex,
    resetRootFocus,
    handleA11yClose,
    setA11yChildRefs,
    handleArrowUp,
    handleArrowDown,
    handleEnterKeyDown,
  } = useDropdownA11y({
    items,
    children,
    dropdownRef,
    onOutsideClick,
  });

  const onOutsideClickRef = useLatestRef(onOutsideClick);

  const handleOutsideClick = useCallback(
    (event: Event) => {
      if (shown) {
        onOutsideClickRef.current?.(event);
      }
    },
    [onOutsideClickRef, shown]
  );

  const { clickOutsideRef: wrapperRef } =
    useClickOutside<HTMLDivElement>(handleOutsideClick);

  useUpdateEffect(() => {
    if (!shown) {
      resetRootFocus();
      handleHide();
    }
  }, [shown]);

  const positionClassNames = position
    .split("-")
    .map((positionPart) => styles[`position-${positionPart}`]);

  return (
    <>
      <div
        ref={wrapperRef}
        className={styles.anchor}
        data-test-id={`${dataTestId}-anchor`}
      >
        <A11y>{children}</A11y>

        {shown && (
          <Flyout
            id={id}
            className={cn(styles.flyout, positionClassNames, flyoutClassName)}
            parentRef={wrapperRef}
            positionsOrder={flyoutPositions}
            onChangePosition={handleChangePosition}
            dataTestId={`${dataTestId}-flyout`}
          >
            <A11yRoot
              onArrowUp={handleArrowUp}
              onArrowDown={handleArrowDown}
              onAfterBlur={handleA11yClose}
              onEnter={handleEnterKeyDown}
            >
              <div className={styles["dropdown-wrapper"]}>
                <ul
                  ref={dropdownCallbackRef}
                  data-test-id={dataTestId}
                  className={cn(
                    styles.dropdown,
                    { [styles["fade-top"]]: hasFadeTop },
                    { [styles["fade-bottom"]]: hasFadeBottom },
                    className
                  )}
                  onScroll={handleScroll}
                >
                  {items.map((itemProps, index) => {
                    const currentEnabledIndex =
                      enabledItemsData.itemIndexes[index];

                    return (
                      <DropdownItem
                        a11yChildRef={
                          currentEnabledIndex >= 0
                            ? setA11yChildRefs(currentEnabledIndex)
                            : undefined
                        }
                        hasA11yFocus={
                          a11yCurrentChildIndex === currentEnabledIndex
                        }
                        key={index}
                        dataTestId={`${dataTestId}-item`}
                        {...itemProps}
                      />
                    );
                  })}
                </ul>
              </div>
            </A11yRoot>
          </Flyout>
        )}
      </div>
      {onOutsideWheel && (
        <div
          className={cn(styles.overlay, {
            [styles["overlay-shown"]]: shown,
          })}
          onWheel={onOutsideWheel}
        />
      )}
    </>
  );
};
